import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';
import {Image} from '@jobseeker/lotus/components/Image';
import {useEffect, useState} from 'react';
import Successful from '@jobseeker/lotus/assets/icons/resources/Successful icon.svg';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

type TransactionDetails = {
  txnId: string;
};

interface SuccessPageProps {
  transactionDetails: TransactionDetails;
}

const SuccessComponent: React.FC<SuccessPageProps> = ({transactionDetails}) => {
  const theme = useWrappedTheme();
  const [paymentData, setPaymentData] = useState('');

  useEffect(() => {
    const storedData = sessionStorage.getItem('paymentData');
    if (storedData) {
      setPaymentData(JSON.parse(storedData));
    }
    console.log('pay', storedData);
  }, []);

  const successMessages = {
    jobseeker_pre_sub_200: 'Pro Membership Plan',
    [`${process.env.NEXT_PUBLIC_APP_NAME}-EA`]: 'Early Applicant',
    [`${process.env.NEXT_PUBLIC_APP_NAME}-RB`]: 'Resume Builder',
    [`${process.env.NEXT_PUBLIC_APP_NAME}-EA_RB`]:
      'Early Applicant and Resume Builder',
  };

  const getSuccessMessage = () =>
    successMessages[paymentData] || 'Transaction Successful';

  const productBenefits = {
    [`${process.env.NEXT_PUBLIC_APP_NAME}-EA`]: [
      'Be the first one to apply for the jobs you want and save precious time.',
      'Fill in your preferences and let our system do the work for you. As soon as a job based on your preferences is posted, we notify you and you can apply for it.',
      'Candidates who apply early have a greater chance of being noticed by employers.',
      'Modify your preferences as many times as you please.',
    ],
    [`${process.env.NEXT_PUBLIC_APP_NAME}-RB`]: [
      'Create a professional resume with a very easy-to-use resume builder.',
      'Stand apart from the competition with visually appealing resumes.',
      'No need to spend hours formatting your CV and entering the details, just choose a template and we will pre-fill details in your resume from your profile. In just a few clicks, you are all set to go!',
      'Once done with the resume, start applying to the jobs with the resume you created using the One-click Upload feature.',
    ],
    jobseeker_pre_sub_200: [
      'Every application of yours marked with an eye-catching star icon',
      'Follow-up against a position where you were a good fit but did not hear back from the recruiter. You get 1 follow-up a week, added every Thursday',
      'Ability to compare your application with respect to other candidates',
      'Real-time updates against applied jobs based on the recruiter action',
      'Initiate chat with the recruiter who has shortlisted your application',
    ],
    [`${process.env.NEXT_PUBLIC_APP_NAME}-EA_RB`]: [
      'Be the first one to apply for the jobs you want and save precious time.',
      'Fill in your preferences and let our system do the work for you. As soon as a job based on your preferences is posted, we notify you and you can apply for it.',
      'Candidates who apply early have a greater chance of being noticed by employers.',
      'Create a professional resume with a very easy-to-use resume builder.',
      'Stand apart from the competition with visually appealing resumes.',
      'No need to spend hours formatting your CV and entering the details, just choose a template and we will pre-fill details in your resume from your profile.',
    ],
    default: [
      'Every application of yours marked with an eye-catching star icon',
      'Follow-up against a position where you were a good fit but did not hear back from the recruiter. You get 1 follow-up a week, added every Thursday',
      'Ability to compare your application with respect to other candidates',
      'Real-time updates against applied jobs based on the recruiter action',
      'Initiate chat with the recruiter who has shortlisted your application',
    ],
  };

  const getProductBenefits = () =>
    productBenefits[paymentData] || productBenefits.default;

  const getBenefitCount = () => {
    const benefits = getProductBenefits();
    return benefits.length;
  };

  return (
    <Box
      sx={{
        backgroundColor: 'border.main',
        minHeight: '891px',
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'center',
        paddingTop: {xs: 1, md: 1.5},
      }}
    >
      <Box
        sx={{
          maxWidth: {xs: '100%', md: 1048},
          width: {xs: '100%', md: 1048},
          height: {xs: 'auto', md: 558},
          padding: {xs: 2, md: theme.spacing(5, 4)},
          backgroundColor: 'primary.contrastText',
          borderRadius: 4,
          boxShadow: {
            xs: '8px 6.48px 6px 2px rgba(41, 41, 41, 0.02)',
            md: '0px 6.48px 6px 2px rgba(41, 41, 41, 0.04)',
          },
          textAlign: 'center',
          marginTop: {xs: 2, md: 0},
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: {xs: 1.5, md: 2},
          }}
        >
          <Image
            src={
              isAppIimjobs
                ? `${process.env.NEXT_PUBLIC_STATIC_IIMJOBS}/static/images/greenCheck.png`
                : Successful
            }
            width={48}
            height={47}
            alt="Success"
          />
        </Box>

        <Typography
          sx={{
            fontSize: {
              xs: theme.typography.pxToRem(16),
              md: theme.typography.pxToRem(18),
            },
            fontWeight: 600,
            lineHeight: '25px',
            color: 'text.primary',
            marginBottom: 1,
          }}
        >
          {process.env.NEXT_PUBLIC_APP_DOMAIN} {getSuccessMessage()} -
          Transaction Successful
        </Typography>

        <Typography
          sx={{
            fontSize: {
              xs: theme.typography.pxToRem(14),
              md: theme.typography.pxToRem(16),
            },
            fontWeight: 500,
            lineHeight: '125%',
            color: 'text.primary',
            marginBottom: {xs: 1.5, md: 2.25},
            maxWidth: {xs: '100%', md: 665},
            margin: {xs: '0 auto', md: '0 auto 0 165px'},
          }}
        >
          Your transaction was successful and your Order No.{' '}
          {transactionDetails.txnId} towards the{' '}
          {process.env.NEXT_PUBLIC_APP_DOMAIN} {getSuccessMessage()} has now
          been processed
        </Typography>

        <Box
          sx={{
            height: '1px',
            backgroundColor: 'border.lightGray',
            width: {xs: 'calc(100% + 30px)', md: 'calc(100% + 60px)'},
            margin: {xs: '18px -15px', md: '25px -26px'},
          }}
        ></Box>

        <Box
          sx={{
            borderRadius: 3,
            padding: {xs: 2, md: 3},
            textAlign: 'left',
            marginBottom: 0,
            marginTop: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: theme.typography.pxToRem(14),
                md: theme.typography.pxToRem(16),
              },
              fontWeight: 600,
              color: 'text.primary',
              marginBottom: 2,
              paddingLeft: {xs: 1, md: 12},
              alignContent: 'center',
              textAlign: {xs: 'left', md: 'left'},
            }}
          >
            We've unlocked {getBenefitCount()} special benefits mentioned below
            that are valid for the next 6 months!
          </Typography>
          <Box
            component="ul"
            sx={{
              fontSize: {
                xs: theme.typography.pxToRem(12),
                md: theme.typography.pxToRem(14),
              },
              fontWeight: 500,
              lineHeight: '24px',
              color: 'text.primary',
              paddingLeft: {xs: 1, md: 12},
              textAlign: {xs: 'left', md: 'left'},
              marginBottom: 0,
              listStylePosition: 'inside',
              marginRight: {xs: 1, md: 21},
              listStylePosition: 'outside',
              marginLeft: '20px',
            }}
          >
            {getProductBenefits().map((benefit, index) => (
              <li key={index}>{benefit}</li>
            ))}
          </Box>
          <Typography
            sx={{
              fontSize: {
                xs: theme.typography.pxToRem(12),
                md: theme.typography.pxToRem(14),
              },
              fontWeight: 500,
              lineHeight: '24px',
              color: 'text.primary',
              marginTop: 2,
              paddingLeft: {xs: 1, md: 12},
              textAlign: {xs: 'left', md: 'left'},
            }}
          >
            Thanks for your continued patronage and here's wishing you all the
            success in your job search.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SuccessComponent;
